import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fetcher } from "@/lib/api";

export default function Suscriptores() {
  const { data, mutate } = useSWR("/subscribers", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", categories: "" });
  const [q, setQ] = useState("");

  const add = async () => {
    try {
      await api.post("/subscribers", {
        email: form.email,
        name: form.name,
        categories: form.categories.split(",").map((s) => s.trim()).filter(Boolean),
        consent: true,
      });
      toast.success("Suscriptor añadido");
      setOpen(false);
      setForm({ email: "", name: "", categories: "" });
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    await api.delete(`/subscribers/${id}`).catch((e) => toast.error(errMsg(e)));
    mutate();
  };

  const rows = (data || []).filter(
    (s) => !q || s.email.includes(q.toLowerCase()) || (s.name || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <PageHeader
        kicker="Audiencia"
        title="Lista de suscripción"
        description="Consentimiento explícito registrado por suscriptor y token de baja en cada correo, conforme a GDPR y buenas prácticas internacionales."
      >
        <Button onClick={() => setOpen(true)} data-testid="new-subscriber-btn">
          <Plus className="h-4 w-4 mr-2" />Añadir suscriptor
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input placeholder="Buscar por correo o nombre…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="subscriber-search" />
        <div className="text-sm text-muted-foreground whitespace-nowrap self-center mono">
          {rows.length} de {data?.length || 0}
        </div>
      </div>

      <div className="border border-border bg-card divide-y divide-border" data-testid="subscribers-list">
        {rows.map((s) => (
          <div key={s.id} className="p-4 flex items-center justify-between gap-3" data-testid={`subscriber-${s.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{s.email}</div>
              <div className="text-xs text-muted-foreground">
                {s.name || "—"} · {(s.categories || []).join(", ") || "todas las categorías"}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Badge variant={s.status === "active" ? "default" : "secondary"}>
                {s.status === "active" ? "Activo" : "Baja"}
              </Badge>
              <Button size="sm" variant="outline" onClick={() => remove(s.id)} data-testid={`delete-subscriber-${s.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground p-8 text-center">Sin suscriptores todavía.</p>
        )}
      </div>

      <div className="border border-border bg-card p-6 mt-6">
        <div className="kicker mb-2">Formulario público de suscripción</div>
        <p className="text-sm text-muted-foreground">
          Comparta este enlace o enlácelo desde sus sitios WordPress. El lector elige los medios y los
          temas que desea recibir, y al confirmar se le ofrecen los canales de WhatsApp y Telegram.
        </p>
        <div className="flex flex-wrap gap-3 mt-4 items-center">
          <Button asChild variant="outline" data-testid="open-public-form-btn">
            <a href="/suscribirse" target="_blank" rel="noreferrer">Abrir formulario</a>
          </Button>
          <code className="mono text-xs p-2 bg-muted overflow-x-auto">
            {window.location.origin}/suscribirse
          </code>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          También puede integrarlo por API:{" "}
          <code className="mono text-xs">POST {process.env.REACT_APP_BACKEND_URL}/api/public/subscribe</code>
          {" "}con {"{ email, name, outlet_ids: [], categories: [], consent: true }"}
        </p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="subscriber-dialog">
          <DialogHeader><DialogTitle className="serif text-2xl">Nuevo suscriptor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider">Correo</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="subscriber-email-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="subscriber-name-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Categorías (coma)</Label>
              <Input value={form.categories} onChange={(e) => setForm({ ...form, categories: e.target.value })} className="mt-1.5" data-testid="subscriber-categories-input" /></div>
          </div>
          <DialogFooter>
            <Button onClick={add} disabled={!form.email} data-testid="save-subscriber-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
